import React from "react";
import { Info, Loader2 } from "lucide-react";

/**
 * Presentation primitives shared by the Cyber screens.
 *
 * Extracted once CyberTabs.jsx appeared: two files defining their own `card` and
 * severity palette drift apart within a release, and a security screen whose
 * "critical" is a different colour on two tabs teaches people to distrust it.
 */

export const card = {
  border: "1px solid var(--cares-line-card)", borderRadius: 14, background: "var(--cares-surface-card)",
  boxShadow: "0 1px 3px rgba(15,23,42,.05)",
};
export const th = {
  textAlign: "left", fontSize: 10.5, fontWeight: 700, color: "var(--cares-text-label)",
  textTransform: "uppercase", letterSpacing: .4, padding: "8px 10px",
  borderBottom: "1px solid var(--cares-line-card)", whiteSpace: "nowrap",
};
export const td = { fontSize: 12, color: "var(--cares-text-2)", padding: "8px 10px",
                    borderBottom: "1px solid var(--cares-line)" };
export const btn = {
  display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 11px",
  borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
  border: "1px solid var(--cares-line-strong)", background: "var(--cares-surface-card)", color: "var(--cares-text-2)",
};

export const SEV = {
  CRITICAL: { bg: "color-mix(in srgb, var(--cares-orange) 22%, var(--cares-surface-card))", fg: "var(--cares-orange)" },
  HIGH: { bg: "color-mix(in srgb, var(--cares-orange) 22%, var(--cares-surface-card))", fg: "var(--cares-orange)" },
  MEDIUM: { bg: "color-mix(in srgb, var(--cares-amber) 22%, var(--cares-surface-card))", fg: "var(--cares-amber)" },
  LOW: { bg: "color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))", fg: "var(--cares-blue)" },
  NONE: { bg: "var(--cares-surface-board)", fg: "var(--cares-text-3)" },
};
export const BAND = {
  CRITICAL: "#dc2626", HIGH: "#ea580c", MODERATE: "#d97706", LOW: "#059669",
};


export const Pill = ({ tone, children }) => (
  <span style={{ background: tone.bg, color: tone.fg, fontSize: 10, fontWeight: 700,
                 padding: "2px 7px", borderRadius: 999, whiteSpace: "nowrap" }}>
    {children}
  </span>
);

export const n = (v) => (v ?? 0).toLocaleString();
export const money = (v) => `$${Math.round(v ?? 0).toLocaleString()}`;

export function Kpi({ label, value, sub, tone = "var(--cares-text-1)", icon: Icon }) {
  return (
    <div style={{ ...card, padding: 14, flex: "1 1 168px", minWidth: 168 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10.5,
                    fontWeight: 700, color: "var(--cares-text-note)", textTransform: "uppercase",
                    letterSpacing: .4 }}>
        {Icon && <Icon size={12} />} {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color: tone, marginTop: 6,
                    lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "var(--cares-text-label)", marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

/** Names which figures are real and which are demonstration data. */
export function Provenance({ provenance, compact }) {
  const [open, setOpen] = React.useState(false);
  if (!provenance) return null;
  return (
    <div style={{ ...card, background: "var(--cares-surface-board)", padding: compact ? "8px 12px" : 12,
                  marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11.5,
                    color: "var(--cares-text-3)" }}>
        <Info size={13} style={{ flexShrink: 0, color: "var(--cares-text-label)" }} />
        <span style={{ flex: 1 }}>{provenance.note}</span>
        <button onClick={() => setOpen((o) => !o)}
                style={{ background: "none", border: "none", color: "var(--cares-purple)",
                         fontSize: 11.5, fontWeight: 600, cursor: "pointer" }}>
          {open ? "Hide" : "What's real?"}
        </button>
      </div>
      {open && (
        <div style={{ display: "flex", gap: 22, marginTop: 10, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--cares-green)",
                          textTransform: "uppercase" }}>From the estate</div>
            <ul style={{ margin: "4px 0 0 16px", padding: 0, fontSize: 11, color: "var(--cares-text-3)" }}>
              {provenance.real.map((r) => <li key={r}>{r}</li>)}
            </ul>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--cares-amber)",
                          textTransform: "uppercase" }}>Generated</div>
            <ul style={{ margin: "4px 0 0 16px", padding: 0, fontSize: 11, color: "var(--cares-text-3)" }}>
              {provenance.generated.map((r) => <li key={r}>{r}</li>)}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export function useAsync(fn, deps) {
  const [state, setState] = React.useState({ loading: true, data: null, error: "" });
  React.useEffect(() => {
    let dead = false;
    setState((s) => ({ ...s, loading: true, error: "" }));
    fn().then((data) => !dead && setState({ loading: false, data, error: "" }))
        .catch((e) => !dead && setState({ loading: false, data: null,
                                          error: e.message || String(e) }));
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
}

export const Loading = () => (
  <div style={{ padding: 40, textAlign: "center", color: "var(--cares-text-note)", fontSize: 13 }}>
    <Loader2 size={18} style={{ marginBottom: 6 }} /> Loading…
  </div>
);
export const Err = ({ children }) => (
  <div style={{ ...card, borderColor: "color-mix(in srgb, var(--cares-orange) 30%, var(--cares-surface-card))", background: "color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))", padding: 14,
                fontSize: 12.5, color: "var(--cares-orange)" }}>{children}</div>
);
